import { useCallback, useEffect, useRef, useState } from 'react'

const isCallable = (value) => typeof value === 'function'

const getSessionStorage = () => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return {
      getItem: () => null,
      setItem: () => {},
      removeItem: () => {},
    }
  }
  return window.sessionStorage
}

const deserialize = (value, fallback) => {
  if (value === null || value === undefined) {
    return fallback
  }

  const trimmed = value.trim()

  if (!trimmed) {
    return ''
  }

  try {
    return JSON.parse(trimmed)
  } catch (_error) {
    return trimmed
  }
}

const serialize = (value) => {
  if (value === undefined || value === null) {
    return null
  }

  if (typeof value === 'string') {
    return value
  }

  try {
    return JSON.stringify(value)
  } catch (_error) {
    return null
  }
}

export const useSessionStorageState = (key, defaultValue) => {
  const storageRef = useRef(getSessionStorage())

  const read = useCallback(() => {
    const storedValue = storageRef.current.getItem(key)
    if (storedValue === null) {
      return defaultValue
    }
    return deserialize(storedValue, defaultValue)
  }, [key, defaultValue])

  const [value, setValue] = useState(read)

  useEffect(() => {
    storageRef.current = getSessionStorage()
    setValue(read())
  }, [key, read])

  const setSessionValue = useCallback(
    (nextValue) => {
      const resolvedValue = isCallable(nextValue) ? nextValue(value) : nextValue
      const serialized = serialize(resolvedValue)

      if (serialized === null || serialized === '') {
        storageRef.current.removeItem(key)
      } else {
        storageRef.current.setItem(key, serialized)
      }

      setValue(resolvedValue)
    },
    [key, value],
  )

  return [value, setSessionValue]
}

export default useSessionStorageState
